import React, { useRef, useState, useEffect } from "react";
import "./SelectableArea.css";

interface ISelectableAreaProps extends React.HTMLAttributes<HTMLDivElement>{
    onRectChangeStart?: (rect: DOMRect, clientPos: {x: number, y: number})=>void;
    onRectChange?: (rect: DOMRect, clientPos: {x: number, y: number})=>void;
    onRectChangeEnd?: (rect: DOMRect,clientPos: {x: number, y: number})=>void;
    isLeftCollapsed?: boolean;
}

export const SelectableArea = ({onRectChangeEnd, onRectChangeStart, onRectChange, isLeftCollapsed, children, className, ...htmlAttributes}: ISelectableAreaProps)=>{
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedRect, setSelectedRect] = useState<DOMRect>(null);

    // Функция для получения текущего смещения в зависимости от состояния панели
    const getLeftPanelOffset = () => isLeftCollapsed ? 300 : 0;

    return <div 
        ref={containerRef}
        className={`SelectableArea ${className || ''}`}
        {...htmlAttributes}
        onMouseDown={(downEvent)=>{
            if (downEvent.buttons != 1){
                return;
            }
            const lastSelect = window.document.body.style.userSelect;
            window.document.body.style.userSelect = 'none';
            const bounds = containerRef.current.getBoundingClientRect();

            // Получаем актуальное смещение в момент начала выделения
            const currentOffset = getLeftPanelOffset();

            // Создаем два прямоугольника: 
            // 1. Для визуального отображения (без смещения)
            // 2. Для логики выделения (с учетом смещения, если левая панель свернута)
            const visualRect = new DOMRect(
                downEvent.clientX - bounds.left,
                downEvent.clientY - bounds.top,
                0,
                0
            );
            const logicalRect = new DOMRect(
                downEvent.clientX - bounds.left + currentOffset,
                downEvent.clientY - bounds.top,
                0,
                0
            );

            setSelectedRect(visualRect);
            onRectChangeStart?.(logicalRect, {x: bounds.left, y: bounds.top});
            onRectChange?.(logicalRect, {x: bounds.left, y: bounds.top});
            const moveHandler = (moveEvent: MouseEvent)=>{
                // Получаем актуальное смещение при каждом движении мыши
                const actualOffset = getLeftPanelOffset();

                // Для визуального отображения без смещения
                const visualRect = new DOMRect(
                    downEvent.clientX - bounds.left,
                    downEvent.clientY - bounds.top,
                    moveEvent.clientX - downEvent.clientX,
                    moveEvent.clientY - downEvent.clientY
                );

                // Для логики выделения с учетом актуального смещения
                const logicalRect = new DOMRect(
                    downEvent.clientX - bounds.left + actualOffset,
                    downEvent.clientY - bounds.top,
                    moveEvent.clientX - downEvent.clientX,
                    moveEvent.clientY - downEvent.clientY
                );
                // Корректировка отрицательных размеров для визуального прямоугольника
                if (visualRect.width < 0){
                    visualRect.x +=  visualRect.width;
                    visualRect.width = -visualRect.width;
                }
                if (visualRect.height < 0){
                    visualRect.y +=  visualRect.height;
                    visualRect.height = -visualRect.height;
                }

                // Корректировка отрицательных размеров для логического прямоугольника
                if (logicalRect.width < 0){
                    logicalRect.x +=  logicalRect.width;
                    logicalRect.width = -logicalRect.width;
                }
                if (logicalRect.height < 0){
                    logicalRect.y +=  logicalRect.height;
                    logicalRect.height = -logicalRect.height;
                }

                setSelectedRect(visualRect);
                onRectChange?.(logicalRect, {x: bounds.left, y: bounds.top});
            }
            const upHandler = (moveEvent: MouseEvent)=>{
                window.removeEventListener('mousemove', moveHandler);
                window.removeEventListener('mouseup', upHandler);
                window.document.body.style.userSelect = lastSelect;

                // При отпускании мыши используем актуальное смещение
                const finalOffset = getLeftPanelOffset();

                // Создаем финальный логический прямоугольник с актуальным смещением
                const finalLogicalRect = selectedRect ? new DOMRect(
                    selectedRect.x + finalOffset,
                    selectedRect.y,
                    selectedRect.width,
                    selectedRect.height
                ) : null;

                onRectChangeEnd?.(finalLogicalRect, {x: bounds.left, y: bounds.top});
                setSelectedRect(null);        
            }
            window.addEventListener('mousemove', moveHandler);
            window.addEventListener('mouseup', upHandler);
        }}
    >
        {children}
        {(selectedRect && (selectedRect.width!=0 || selectedRect.height!=0)) && <div className="SelectableArea_rect"
            style={{
                top: selectedRect.top + 'px',
                left: selectedRect.left + 'px',
                width: selectedRect.width + 'px',
                height: selectedRect.height + 'px'
            }}
        ></div>}
    </div>
}